// JWT Authentication - Using real backend API
import { loginSuccess, logoutUserSuccess, apiError, reset_login_flag } from './reducer';
import { dashboardAPI } from '../../services/api';
import { setAuthorization } from '../../helpers/api_helper';
import axios from 'axios';

export const loginUser = (user: any, history: any) => async (dispatch: any) => {   
  try {
    // Check if this is the demo account - skip backend request
    const isDemoAccount = user.email === 'admin@demo.com.tr' && user.password === 'Demo123.';
    
    let loginData: any;
    let accessToken: string | null = null;
    
    if (isDemoAccount) {
      // Demo account - return mock data without backend call
      console.log('🔐 Using demo account - skipping backend request');
      
      accessToken = 'demo-token-' + Date.now();
      
      // Create demo user data structure
      const demoUserData = {
        userId: 'demo-user-id',
        email: 'admin@demo.com.tr',
        username: 'admin@demo.com.tr',
        roles: ['ROLE_COMPANY_ADMIN'],
        companyId: 'demo-company-id',
        storeIds: ['demo-store-1', 'demo-store-2'],
        firstName: 'Demo',
        lastName: 'Admin'
      };
      
      // Store demo user data
      const authUserData = {
        ...demoUserData,
        token: accessToken,
        access_token: accessToken,
        refresh_token: 'demo-refresh-token',
        id: demoUserData.userId
      };
      
      sessionStorage.setItem("authUser", JSON.stringify(authUserData));
      dispatch(loginSuccess(authUserData));
      history("/dashboard");
      return; // Exit early - no backend call needed
    }
    
    // Regular account - use JWT authentication via real backend API
    console.log('🔐 Attempting login with:', { username: user.email, passwordLength: user.password?.length });
    const response: any = await dashboardAPI.login({
      username: user.email,
      password: user.password
    });
    console.log('✅ Login response received:', response);

    // Handle both AxiosResponse and direct data response
    loginData = response?.data || response;

    // Backend returns access_token (not token) and doesn't include user data
    accessToken = loginData?.access_token || loginData?.token;
    
    if (accessToken) {
      // Store token in axios defaults for future requests
      setAuthorization(accessToken);
      
      // Update axios defaults immediately
      axios.defaults.headers.common["Authorization"] = "Bearer " + accessToken;
      
      // Fetch user data from /auth/me endpoint
      // IMPORTANT: Clear any old demo data from sessionStorage first to ensure isDemoUser() returns false
      try {
        // Temporarily clear sessionStorage to ensure getCurrentUser() makes real API call
        const tempStorage = sessionStorage.getItem("authUser");
        sessionStorage.removeItem("authUser");
        
        // Now call getCurrentUser - it will see no sessionStorage and make real API call
        const userResponse: any = await dashboardAPI.getCurrentUser();
        const userData = userResponse?.data || userResponse;
        
        console.log('✅ Fetched real user data from /auth/me:', userData);
        
        // Store ONLY the real user data from backend with token
        const authUserData = {
          ...userData, // Use all data from backend (Keycloak)
          token: accessToken,
          access_token: accessToken,
          refresh_token: loginData?.refresh_token
        };
        
        // Ensure email and username are from backend, not from login form
        if (userData.email) {
          authUserData.email = userData.email;
        }
        if (userData.username) {
          authUserData.username = userData.username;
        }
        
        sessionStorage.setItem("authUser", JSON.stringify(authUserData));
        dispatch(loginSuccess(authUserData));
        history("/dashboard");
      } catch (userError: any) {
        console.error('❌ Failed to fetch user data:', userError);
        // If fetching user fails, still store token and basic info from login form
        const authUserData = {
          email: user.email,
          username: user.email,
          token: accessToken,
          access_token: accessToken,
          refresh_token: loginData?.refresh_token
        };
        
        sessionStorage.setItem("authUser", JSON.stringify(authUserData));
        dispatch(loginSuccess(authUserData));
        history("/dashboard");
      }
    } else {
      dispatch(apiError({ data: "Geçersiz kimlik bilgileri" }));
    }
  } catch (error: any) {
    // Extract error message from response
    let errorMessage = "Giriş başarısız. Lütfen tekrar deneyin.";
    
    // Handle 401 Unauthorized (invalid credentials)
    if (error?.response?.status === 401) {
      console.error('❌ 401 Unauthorized error during login:', error);
      console.error('❌ Error response data:', error?.response?.data);
      console.error('❌ Error response status:', error?.response?.status);
      
      const errorData = error.response?.data || error.response || {};
      if (errorData?.message) {
        errorMessage = errorData.message;
      } else if (errorData?.userMessage) {
        errorMessage = errorData.userMessage;
      } else if (typeof errorData === 'string') {
        errorMessage = errorData;
      } else {
        errorMessage = "Geçersiz kimlik bilgileri. Lütfen kullanıcı adı ve şifrenizi kontrol edin.";
      }
      
      // Translate common 401 messages to Turkish
      if (errorMessage.toLowerCase().includes('invalid credentials') || 
          errorMessage.toLowerCase().includes('unauthorized') ||
          errorMessage.toLowerCase().includes('bad credentials')) {
        errorMessage = "Geçersiz kimlik bilgileri. Lütfen kullanıcı adı ve şifrenizi kontrol edin.";
      }
      
      console.error('❌ Final error message:', errorMessage);
      dispatch(apiError({ data: errorMessage }));
      return;
    }
    
    // Handle 400 Bad Request with validation errors
    if (error?.response?.status === 400) {
      const errorData = error.response.data;
      
      // Check if there are validation details in additionalInfo
      if (errorData?.additionalInfo && typeof errorData.additionalInfo === 'object') {
        // Extract validation messages and translate to Turkish
        const validationMessages: string[] = [];
        
        Object.keys(errorData.additionalInfo).forEach((field) => {
          const fieldMessage = errorData.additionalInfo[field];
          let translatedMessage = fieldMessage;
          
          // Translate common validation messages to Turkish
          if (fieldMessage.includes('Password must be between')) {
            translatedMessage = 'Şifre 6 ile 128 karakter arasında olmalıdır';
          } else if (fieldMessage.includes('must not be empty') || fieldMessage.includes('required')) {
            translatedMessage = `${field === 'password' ? 'Şifre' : field === 'username' ? 'Kullanıcı adı' : field} gereklidir`;
          } else if (fieldMessage.includes('must be a valid')) {
            translatedMessage = `${field === 'email' ? 'Geçerli bir e-posta adresi' : 'Geçerli bir değer'} giriniz`;
          } else if (fieldMessage.includes('too short')) {
            translatedMessage = 'Çok kısa';
          } else if (fieldMessage.includes('too long')) {
            translatedMessage = 'Çok uzun';
          }
          
          validationMessages.push(translatedMessage);
        });
        
        if (validationMessages.length > 0) {
          errorMessage = validationMessages.join('. ');
        } else if (errorData?.userMessage) {
          // Use userMessage if available
          errorMessage = errorData.userMessage;
        } else if (errorData?.message) {
          // Translate generic validation message
          if (errorData.message === 'Validation failed') {
            errorMessage = 'Doğrulama hatası. Lütfen bilgilerinizi kontrol edin.';
          } else {
            errorMessage = errorData.message;
          }
        }
      } else if (errorData?.userMessage) {
        errorMessage = errorData.userMessage;
      } else if (errorData?.message) {
        if (errorData.message === 'Validation failed') {
          errorMessage = 'Doğrulama hatası. Lütfen bilgilerinizi kontrol edin.';
        } else {
          errorMessage = errorData.message;
        }
      }
    } else if (error?.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error?.message) {
      errorMessage = error.message;
    } else if (typeof error === 'string') {
      errorMessage = error;
    }
    
    // Translate common error messages to Turkish
    if (errorMessage.toLowerCase().includes('invalid credentials') || 
        errorMessage.toLowerCase().includes('geçersiz kimlik')) {
      errorMessage = "Geçersiz kimlik bilgileri";
    }
    
    dispatch(apiError({ data: errorMessage }));
  }
};

export const logoutUser = (history?: any) => async (dispatch: any) => {
  try {
    // Clear all storage
    sessionStorage.clear(); // Clear all sessionStorage, not just authUser
    localStorage.removeItem("authUser");
    
    // Clear axios authorization header
    delete axios.defaults.headers.common["Authorization"];
    
    // Dispatch logout success
    dispatch(logoutUserSuccess(true));
    
    // Redirect to login page if history is provided
    if (history) {
      history("/login");
    } else {
      // Fallback: redirect using window.location
      window.location.href = process.env.PUBLIC_URL + "/login" || "/login";
    }
  } catch (error) {
    dispatch(apiError(error));
  }
};

// Social login removed - using JWT only
// If needed in future, implement via backend OAuth endpoints
export const socialLogin = (type: any, history: any) => async (dispatch: any) => {
  try {
    // Social login not supported - redirect to regular login
    dispatch(apiError({ data: "Social login not available. Please use email and password." }));
    history('/login');
  } catch (error) {
    dispatch(apiError(error));
  }
};

export const resetLoginFlag = () => async (dispatch: any) => {
  try {
    dispatch(reset_login_flag());
  } catch (error) {
    dispatch(apiError(error));
  }
};